import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import styles from './Navbar.module.scss';
import synnexLogoWhite from '../../Assets/Images/Synnex-logo-white.svg';
import synnexLogoDark from '../../Assets/Images/synnex-logo-dark.svg';
import ThemeSwitcher from '../../Components/ThemeSwitcher';
import ProfileIcon from '../../Components/Profile';
import { userApi } from '../../Utils/API';
import useOutsideClick from '../../hooks/useOutsideClick';
import loadingGif from '../../Assets/Animations/loading.gif';
import SearchResultItem from './SearchResultItem';
import { slugify } from '../../Modules/Authorised/ApiReference/utils/api-parser';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [apiContent, setApiContent] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useOutsideClick([searchRef, dropdownRef], () => {
    setIsDropdownOpen(false);
  });

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === 'k') {
        event.preventDefault();
        searchRef.current.querySelector('input').focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (searchQuery.length < 3) {
      setSearchResults([]);
      setIsDropdownOpen(false);
      return;
    }

    const debounceTimer = setTimeout(() => {
      fetchSearchResults();
    }, 300);

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const fetchSearchResults = async () => {
    setIsLoading(true);
    setIsDropdownOpen(true);
    const token_type = localStorage.getItem("token_type");
    const access_token = localStorage.getItem("access_token");
  
    if (!token_type || !access_token) {
      setIsLoading(false);
      return;
    }
  
    try {
      const response = await userApi.get(`/api/custom-search?term=${searchQuery}`, {
        headers: {
          Authorization: `${token_type} ${access_token}`,
        },
      });
  
      const items = response.data.items || [];
      const newApiContent = {};
      const processedResults = items.flatMap(item => {
        if (item.contentType === 'APIReference' && (item.title === 'Partner Endpoints' || item.title === 'Authentication')) {
          try {
            const spec = JSON.parse(item.preview);
            newApiContent[item.contentItemId] = spec;
            const endpointResults = [];
            const lowerCaseQuery = searchQuery.toLowerCase();

            const searchSchema = (schema, spec) => {
              if (!schema) return false;
          
              // Handle $ref by resolving it
              if (schema.$ref) {
                  const refPath = schema.$ref.split('/');
                  const refName = refPath[refPath.length - 1];
                  return searchSchema(spec.components.schemas[refName], spec);
              }
              
              if (schema.description && schema.description.toLowerCase().includes(lowerCaseQuery)) {
                  return true;
              }
          
              if (schema.properties) {
                  for (const propName in schema.properties) {
                      const prop = schema.properties[propName];
                      if (prop.description && prop.description.toLowerCase().includes(lowerCaseQuery)) {
                          return true;
                      }
                      if (prop.type === 'object' || prop.$ref) {
                          if (searchSchema(prop, spec)) {
                              return true;
                          }
                      }
                  }
              }
              
              if (schema.type === 'array' && schema.items) {
                  if (searchSchema(schema.items, spec)) {
                      return true;
                  }
              }
          
              return false;
            };

            const isMatch = (endpoint, path, spec) => {
                if (!endpoint) return false;

                const { summary, description, parameters, tags, requestBody, responses } = endpoint;

                if (
                    (summary && summary.toLowerCase().includes(lowerCaseQuery)) ||
                    (description && description.toLowerCase().includes(lowerCaseQuery)) ||
                    (path && path.toLowerCase().includes(lowerCaseQuery))
                ) {
                    return true;
                }

                if (parameters) {
                    for (const param of parameters) {
                        if (
                            (param.name && param.name.toLowerCase().includes(lowerCaseQuery)) ||
                            (param.description && param.description.toLowerCase().includes(lowerCaseQuery))
                        ) {
                            return true;
                        }
                    }
                }

                if (tags && tags.some(tag => tag.toLowerCase().includes(lowerCaseQuery))) {
                    return true;
                }
                
                if (requestBody) {
                    if (requestBody.description && requestBody.description.toLowerCase().includes(lowerCaseQuery)) {
                        return true;
                    }
                    if (requestBody.content && requestBody.content['application/json']) {
                        if (searchSchema(requestBody.content['application/json'].schema, spec)) {
                            return true;
                        }
                    }
                }
                
                if (responses) {
                    for (const statusCode in responses) {
                        const response = responses[statusCode];
                        if (response.description && response.description.toLowerCase().includes(lowerCaseQuery)) {
                            return true;
                        }
                        if (response.content && response.content['application/json']) {
                            if (searchSchema(response.content['application/json'].schema, spec)) {
                                return true;
                            }
                        }
                    }
                }

                return false;
            };

            for (const path in spec.paths) {
              for (const method in spec.paths[path]) {
                const endpoint = spec.paths[path][method];
                if (isMatch(endpoint, path, spec)) {
                  endpointResults.push({
                    ...item,
                    preview: `${endpoint.tags[0]} - ${endpoint.summary}`,
                    operationId: endpoint.operationId,
                    contentItemId: `${item.contentItemId}-${endpoint.operationId}`,
                  });
                }
              }
            }
            if (endpointResults.length > 0) {
              return endpointResults;
            }
            // If no endpoints match, return a summary of the API group
            const previewText = spec.info?.description || `A collection of APIs related to ${spec.info?.title || item.title}.`;
            return [{
              ...item,
              preview: previewText,
            }];
          } catch (e) {
            console.error("Failed to parse API spec from search result:", e);
            // Fallback for any parsing errors
            return [{ ...item, preview: `Explore APIs for ${item.title}` }];
          }
        }
        return [item];
      });
  
      setApiContent(prev => ({ ...prev, ...newApiContent }));
      setSearchResults(processedResults);
    } catch (error) {
      console.error('Error fetching search results:', error);
      setSearchResults([]);
      setIsDropdownOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResultClick = (result) => {
    setSearchQuery('');
    setIsDropdownOpen(false);
  
    // Specific content types must be checked BEFORE the generic .path
    if (result.contentType === 'Changelog') {
      navigate('/change-logs', { state: { title: result.title } });
      return;
    }

    if (result.operationId && (result.title === 'Partner Endpoints' || result.title === 'Authentication')) {
      const originalItemId = result.contentItemId.split(`-${result.operationId}`)[0];
      const spec = apiContent[originalItemId];
      if (spec) {
        for (const path in spec.paths) {
          for (const method in spec.paths[path]) {
            const endpoint = spec.paths[path][method];
            if (endpoint.operationId === result.operationId) {
              const apiSlug = slugify(endpoint.summary);
              const subGroupSlug = slugify(endpoint.tags[0]);
              const mainGroupSlug = slugify(result.title);
              
              navigate(`/api-reference/${mainGroupSlug}/${subGroupSlug}/${apiSlug}`);
              return;
            }
          }
        }
      }
    }
  
    if (result.path) {
      navigate(result.path);
    } else if (result.contentType === 'APIReference' && !result.operationId) {
      // This handles the case for "Authentication" when no specific endpoint is matched
      const spec = apiContent[result.contentItemId];
      if (spec && spec.paths && Object.keys(spec.paths).length > 0) {
        // Navigate to the first endpoint in the spec
        const firstPath = Object.keys(spec.paths)[0];
        const firstMethod = Object.keys(spec.paths[firstPath])[0];
        const endpoint = spec.paths[firstPath][firstMethod];
        if (endpoint) {
          const apiSlug = slugify(endpoint.summary);
          const subGroupSlug = slugify(endpoint.tags[0]);
          const mainGroupSlug = slugify(result.title);
          navigate(`/api-reference/${mainGroupSlug}/${subGroupSlug}/${apiSlug}`);
          return; // Important: exit after navigating
        }
      }
      // Fallback if the spec can't be processed or has no paths
      const mainGroupSlug = slugify(result.title);
      navigate(`/api-reference/${mainGroupSlug}`);
    } else if (result.contentType === 'APIReference') {
      const mainGroupSlug = slugify(result.title);
      navigate(`/api-reference/${mainGroupSlug}`);
    }
  };

  const getNavLinkClass = ({ isActive }) => {
    return isActive ? `${styles.navLink} ${styles.active}` : styles.navLink;
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header className={styles.header}>
      <div className={styles.headerLeft}>
        <div className={styles.logo}>
          <img src={synnexLogoWhite} alt="Synnex Logo" className={styles.logoWhite} />
          <img src={synnexLogoDark} alt="Synnex Logo" className={styles.logoDark} />
        </div>
        <nav className={`${styles.nav} ${isMenuOpen ? styles.navOpen : ''}`}>
          <ul className={styles.navList}>
            <li><NavLink to="/guides" className={getNavLinkClass} onClick={toggleMenu}><i className="material-icons">book</i><span className='text-md'>Guides</span></NavLink></li>
            <li><NavLink to="/workflows" className={getNavLinkClass} onClick={toggleMenu}><i className="material-icons">hub</i><span className='text-md'>Workflows</span></NavLink></li>
            <li><NavLink to="/api-reference" className={getNavLinkClass} onClick={toggleMenu}><i className="material-icons">code</i><span className='text-md'>API Reference</span></NavLink></li>
            <li><NavLink to="/change-logs" className={getNavLinkClass} onClick={toggleMenu}><i className="material-icons">history</i><span className='text-md'>Changelog</span></NavLink></li>
          </ul>
        </nav>
      </div>
      <div className={styles.headerRight}>
      <div className={styles.search} ref={searchRef}>
      <i className={`material-icons ${styles.searchIcon}`}>search</i>
          <input
            type="text"
            placeholder="Search"
             value={searchQuery}
             onChange={(e) => setSearchQuery(e.target.value)}
             onFocus={() => setIsSearchFocused(true)}
             onBlur={() => setIsSearchFocused(false)}
           />
           <div className={styles.searchControls}>
             {searchQuery && (
               <button className={styles.clearButton} onClick={() => setSearchQuery('')}>
                 <i className="material-icons">close</i>
               </button>
             )}
             {!isSearchFocused && !searchQuery && !isLoading && <span className={styles.shortcut}>CTRL-K</span>}
           </div>
           {isDropdownOpen && (
             <div className={styles.searchResults} ref={dropdownRef}>
               {isLoading ? (
                 <div className={styles.loadingContainer}>
                   <img src={loadingGif} alt="Loading..." className={styles.searchLoading} />
                 </div>
               ) : searchResults.length > 0 ? (
                 searchResults.map((result) => (
                   <SearchResultItem key={result.contentItemId} result={result} onResultClick={handleResultClick} />
                 ))
               ) : (
                 <div className={styles.noResults}>
                   <i className="material-icons">search_off</i>
                   <span>No results found.</span>
                 </div>
               )}
             </div>
           )}
        </div>
        <ThemeSwitcher />
        <ProfileIcon />
        <button className={styles.hamburger} onClick={toggleMenu}>
          <i className="material-icons">{isMenuOpen ? 'close' : 'menu'}</i>
        </button>
      </div>
    </header>
  );
};

export default Navbar;